import { genres, ratingOptions, regionOptions, yearOptions } from '@/mocks/movies';

interface FilterPanelProps {
  selectedGenres: string[];
  year: string;
  region: string;
  minRating: number;
  onToggleGenre: (genre: string) => void;
  onYearChange: (year: string) => void;
  onRegionChange: (region: string) => void;
  onRatingChange: (rating: number) => void;
  onReset: () => void;
}

function sectionTitle(label: string) {
  return <p className="eyebrow text-[10px] text-foreground-500">{label}</p>;
}

export default function FilterPanel({
  selectedGenres,
  year,
  region,
  minRating,
  onToggleGenre,
  onYearChange,
  onRegionChange,
  onRatingChange,
  onReset,
}: FilterPanelProps) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 text-sm text-foreground-900">
          <i className="ri-equalizer-line text-base text-primary-500" aria-hidden="true"></i>
          筛选条件
        </p>
        <button
          type="button"
          onClick={onReset}
          className="flex items-center gap-1 whitespace-nowrap text-[11px] text-foreground-500 transition-colors hover:text-primary-500"
        >
          <i className="ri-refresh-line" aria-hidden="true"></i>
          重置
        </button>
      </div>

      <div>
        {sectionTitle('影片类型')}
        <div className="mt-3 flex flex-wrap gap-2">
          {genres.map((genre) => {
            const active = selectedGenres.includes(genre.name);
            return (
              <button
                key={genre.name}
                type="button"
                onClick={() => onToggleGenre(genre.name)}
                aria-pressed={active}
                className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition-colors ${
                  active
                    ? 'border-primary-500 bg-primary-500/15 text-primary-500'
                    : 'border-background-300/70 text-foreground-600 hover:border-primary-500/60 hover:text-foreground-900'
                }`}
              >
                {genre.name}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        {sectionTitle('上映年份')}
        <div className="mt-3 flex flex-wrap gap-2">
          {yearOptions.map((option) => {
            const active = year === option;
            return (
              <button
                key={option}
                type="button"
                onClick={() => onYearChange(option)}
                aria-pressed={active}
                className={`whitespace-nowrap rounded-md border px-2.5 py-1 text-xs transition-colors ${
                  active
                    ? 'border-primary-500 bg-primary-500/15 text-primary-500'
                    : 'border-background-300/70 text-foreground-600 hover:border-primary-500/60 hover:text-foreground-900'
                }`}
              >
                {option.replace('全部年份', '全部')}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        {sectionTitle('制片地区')}
        <div className="mt-3 flex flex-wrap gap-2">
          {regionOptions.map((option) => {
            const active = region === option;
            return (
              <button
                key={option}
                type="button"
                onClick={() => onRegionChange(option)}
                aria-pressed={active}
                className={`whitespace-nowrap rounded-md border px-2.5 py-1 text-xs transition-colors ${
                  active
                    ? 'border-primary-500 bg-primary-500/15 text-primary-500'
                    : 'border-background-300/70 text-foreground-600 hover:border-primary-500/60 hover:text-foreground-900'
                }`}
              >
                {option.replace('全部地区', '全部')}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        {sectionTitle('评分区间')}
        <div className="mt-3 flex flex-col gap-2">
          {ratingOptions.map((option) => {
            const active = minRating === option.value;
            return (
              <button
                key={option.label}
                type="button"
                onClick={() => onRatingChange(option.value)}
                aria-pressed={active}
                className={`flex items-center justify-between rounded-md border px-3 py-2 text-xs transition-colors ${
                  active
                    ? 'border-primary-500 bg-primary-500/15 text-primary-500'
                    : 'border-background-300/70 text-foreground-600 hover:border-primary-500/60 hover:text-foreground-900'
                }`}
              >
                <span>{option.label}</span>
                <span className="flex items-center gap-0.5">
                  <i className="ri-star-fill text-[10px]" aria-hidden="true"></i>
                  {option.value > 0 ? option.value.toFixed(1) : 'ALL'}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}